import React, { ChangeEvent, FocusEvent, KeyboardEvent, useEffect, useState } from 'react';

import { CircularProgress, TextField } from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import { Autocomplete, AutocompleteRenderOptionState } from '@material-ui/lab';

import styles from './styles.module.scss';
import { HelperText } from './HelperText';
import { useLocation } from 'react-router';
import { SearchConfig } from '../../utils/constants';
import { useTranslation } from 'react-i18next';

const maxChar = SearchConfig.MaxCharsAllowed;
const minChar = SearchConfig.MinCharsRequired;
let timeoutId: NodeJS.Timeout;

interface SearchFilterProps<T> {
  className?: string;
  onChangeCallBack: (q: string, fieldClosed?: boolean) => void;
  placeholder?: string;
  renderItem: (item: T, selectedState: AutocompleteRenderOptionState) => React.ReactNode;
  onSelect: (item: T) => void;
  handleEnterKeyPress: (value: string) => void;
  results: Maybe<T[]>,
  isLoading: boolean,
  optionClass?: string,
  popperRootClass?: string
}

export type HelperConfigType = 'invalid' | 'noresults' | Falsy;

const validateSearchString = (value: string): boolean => {
  let valLen: number = value.length;
  return !(valLen < minChar || valLen > maxChar);
};

export const SearchFilter = <T, >(props: SearchFilterProps<T>) : JSX.Element => {
  const { className = '',
    onChangeCallBack,
    onSelect,
    placeholder,
    renderItem,
    handleEnterKeyPress,
    results,
    isLoading,
    optionClass,
    popperRootClass
  } = props;
  const [isOpen, setOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const location = useLocation();
  const [t] = useTranslation();

  useEffect(() => {
    reset();
  }, [location]);

  const reset = (): void => {
    setInputValue('');
    setOpen(false);
  }

  const searchFieldChangeHandler = (event: ChangeEvent | FocusEvent) => {
    let target = event.target as HTMLTextAreaElement | HTMLInputElement,
      q = target.value || '';
    setInputValue(q);
    if (validateSearchString(q)) {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        onChangeCallBack(q);
      }, SearchConfig.Debounce);
    } else {
      if (timeoutId) clearTimeout(timeoutId);
      onChangeCallBack('');
    }
  };


  const handleKeyDownEvent = (event: KeyboardEvent) => {
    const key = event.key;
    if (key === 'Enter'){
      let target = event.target as HTMLTextAreaElement | HTMLInputElement,
        value = target.value || '';
      if (validateSearchString(value)){
        handleEnterKeyPress(value)
      }
    }
  }

  const handleClose = (e: any, reason: string) => {
    if (reason !== 'blur' && !validateSearchString(inputValue)) return;
    setOpen(false);
    onChangeCallBack('');
  }

  const getOptions = () => {
    return results as T[];
  }

  const getHelperTextConfig = ():HelperConfigType => {
    if (isOpen){
      if (validateSearchString(inputValue) && results && results.length === 0){
        return 'noresults';
      }else if (!validateSearchString(inputValue)){
        return 'invalid';
      }
    }else{
      return false;
    }
  }

  const onSelectionChange = (_: React.ChangeEvent<{}>, selection: string | T | null): void => {
    if (!selection) return;
    onSelect(selection as T);
  };

  const helperTextConfig = getHelperTextConfig();

  return (
    <Autocomplete
      id= 'search-box'
      classes={{popper: popperRootClass, option: optionClass}}
      filterOptions={list => list} // this needed to list all results. material-ui, will by default filter out options that don't contain the query string
      freeSolo
      open={isOpen}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={handleClose}
      onChange={onSelectionChange}
      options={getOptions()}
      loading={isLoading}
      renderOption={renderItem}
      disableClearable={false}
      inputValue={inputValue}
      role='search'
      renderInput={params => (
        <TextField
          variant="outlined"
          InputProps={{
            startAdornment: isLoading ? <CircularProgress color="inherit" size={20} /> : <SearchIcon />
          }}
          classes={{ root: styles.input }}
          inputProps={params.inputProps}
          onChange={searchFieldChangeHandler}
          onFocus={searchFieldChangeHandler}
          onKeyDown={handleKeyDownEvent}
          ref={params.InputProps.ref}
          helperText={helperTextConfig && <HelperText config={helperTextConfig}/>}
          placeholder={placeholder}
          role='search'
          aria-label= {t('app_search.app_search_help_text', {
            min: minChar,
            max: maxChar
          })}
        />
      )}
    />
  );
};
